/**
 * Typed `track()` wrapper over PostHog's `capture()`.
 *
 * Why bother:
 *  - Event names are checked at compile time. A typo in a flow's
 *    `track('a2p_step_enterd', ...)` fails the build instead of polluting
 *    funnels with unmatchable event names.
 *  - Single seam to add filters / sampling / route to multiple sinks
 *    later without touching call sites.
 *  - Inert when PostHog isn't initialized — flows can call `track()`
 *    unconditionally without null-checking.
 */
import { getPostHog } from "@/lib/posthog";
import type {
  LockReason,
  Phase,
  SubscriptionState,
} from "@/lib/getStarted/curriculum";
import type { AudiencePreset, AudiencePresetSource } from "@/lib/sms/audiencePresets";

type AnyProps = Record<string, unknown>;

export interface EventMap {
  // A2P 10DLC registration wizard
  a2p_wizard_opened: { resumeStep?: string; isFixMode?: boolean };
  a2p_step_entered: { step: string };
  a2p_step_save_failed: { step: string; errorMessage: string };
  a2p_submit_attempted: { registrationType?: string };
  a2p_submit_succeeded: { registrationType?: string };
  a2p_submit_failed: { errorCode?: string; errorMessage?: string };

  // A2P rejection journey (post-Twilio-async-review).
  // WI-09 (M12): the a2p_retry_* / a2p_retry_brand_* event families were
  // deleted — they were declared but never fired, and the A2P page has NO
  // self-serve retry affordance (brand/campaign FAILED states route to
  // support). Re-add them alongside real retry UI, not before.
  a2p_rejection_seen: { failedComponent?: string; lastError?: string };

  // AI Dialer
  dialer_run_started: { campaignId: string; listSize?: number; tier?: string };
  dialer_run_completed: { campaignId: string; callsPlaced?: number; durationSec?: number };
  dialer_run_aborted: { campaignId: string; reason?: string };

  // Automation builder — "Voice Console". Every event carries sequenceId so an
  // adapter/autosave spike is debuggable; success+latency+adoption give the
  // denominators a flagged rollout needs (not just errors).
  automation_builder_console_loaded: { sequenceId: string };
  automation_builder_autosave_succeeded: { sequenceId: string; latencyMs: number };
  automation_builder_autosave_failed: { sequenceId: string; status?: number };
  automation_builder_autosave_drift_400: { sequenceId: string };
  automation_builder_autosave_conflict_409: { sequenceId: string };
  automation_builder_valid_gating_skipped_put: { sequenceId: string };
  automation_builder_publish_failed: { sequenceId: string; status?: number; errorMessage?: string };
  // Leave guard (2026-09-20): he chose "Discard changes" and the draft went back to the live version.
  automation_builder_draft_discarded: { sequenceId: string };
  /**
   * A2 — the activation event. Without it the funnel instruments both ends and every
   * failure but nothing on the step being measured. NOTE: console_loaded (the
   * denominator) fires per MOUNT, so dedupe by PostHog's $session_id at query time
   * rather than dropping events at emit. latencyMs is the client-observed round trip
   * (fetch start → response parsed), not server time.
   */
  automation_builder_publish_succeeded: { sequenceId: string; version?: number; latencyMs?: number };
  // Publish succeeded but the backend returned advisory warnings (replaced another
  // automation, no lead source connected, trigger not live for this account…).
  automation_builder_publish_warned: { sequenceId: string; count: number };
  // automation_builder_enroll_succeeded was emitted only by the builder's "Enroll & run"
  // control, removed 2026-09-08. Its sole emitter is gone, so the event FLATLINES from that
  // date — any PostHog insight or funnel built on it needs re-pointing, not re-instrumenting.
  automation_builder_adapter_exception: { sequenceId: string; message?: string };

  // Automations home — create-flow funnel (UX restructure, spec 2026-07-01 §3.2).
  // Answers: did collapsing create hurt first-conversion? which path do users take?
  // `viewed` fires once per page view WITH the resolved layout — the exposure
  // denominator for BOTH variants (zero-state users never click "open create", so
  // create_opened alone cannot measure exposure).
  automations_home_viewed: { layout: "zero" | "returning" };
  // kind is present only for "New follow-up sequence"; a plain create keeps sending {}.
  automations_home_create_opened: { kind?: "follow_up" };
  // StartPanel inside the builder (list-first home, 2026-09-06): which starting point a
  // brand-new draft took. `dismissed` = the user closed the panel to build by hand.
  automation_builder_incomplete_step_discarded: { sequenceId: string; nodeType: string };
  // Build with AI as a conversation (2026-09-21). A failed AI request used to be invisible
  // to PostHog: the only AI event fired at apply time. Counts only — never what was typed.
  automation_builder_ai_request: {
    sequenceId: string; mode: "generate" | "edit"; turn: number; latencyMs: number; ok: boolean;
    code?: string; added?: number; removed?: number; changed?: number; notes?: number;
  };
  automation_builder_ai_undo: { sequenceId: string; turn: number };
  automation_builder_start_path: { sequenceId: string; path: "ai" | "ai_opened" | "template" | "dismissed"; templateKey?: string; kind?: "automation" | "follow_up" };
  // The home page creates a blank automation ("scratch") or a blank follow-up sequence
  // ("follow_up", 2026-09-16) — "ai"/"template" moved to automation_builder_start_path above.
  // Unions trimmed to what actually fires so a dashboard never waits on an event that cannot arrive.
  automations_home_create_path: { path: "scratch" | "follow_up" };

  // SMS bulk send (broadcast funnel)
  sms_broadcast_send_attempted: { recipientCount: number; hasMedia?: boolean };
  sms_broadcast_send_succeeded: { recipientCount: number; messagesSent?: number; messagesFailed?: number; deferredCount?: number; durationMs?: number };
  sms_broadcast_send_failed: { errorCode?: string; errorMessage?: string; stage?: "preflight" | "send" | "unknown" };
  // Outcome-matrix observability (2026-06-29 banner/broadcastId-race spec, §7).
  sms_blast_banner_shown: { broadcastId: string; total: number };
  sms_blast_fallback_shown: { total: number; deferredCount: number };
  sms_blast_all_deferred_shown: { deferredCount: number };
  // Bounded-existence guard (SmsBroadcastProgress.tsx) — fires when a
  // broadcast row never returns a single ok poll within EXISTENCE_TIMEOUT_MS.
  sms_blast_guard_fired: { broadcastId: string; elapsedMs: number };
  // Rep collapsed/expanded the blast progress banner (SmsBroadcastProgress.tsx) —
  // answers "the banner disappeared" reports.
  sms_blast_banner_minimized: { broadcastId: string; minimized: boolean; status: string };
  // Post-call AI summary popup folded to its one-line bar (true) or opened again (false).
  call_summary_popup_minimized: { summaryId: string; minimized: boolean };
  sms_blast_unexpected_response_shape: { hasBroadcastId: boolean; total: number; deferredCount: number };
  // The /billing/features fetch settled without a usable answer, so /sms-sender's reply
  // plan failed OPEN (shows the picker to a tenant we cannot confirm is entitled). The UI
  // deliberately hides this, so it is only visible here. 2026-08-16.
  sms_reply_plan_entitlement_degraded: {
    reason: "features_fetch_failed" | "features_payload_partial";
  };
  // Scheduled SMS blasts (flag-gated). 2026-06-13. Per-zone waves: 2026-06-14.
  scheduled_blast_scheduled: { campaignCount: number; estimatedCount: number; timezone?: string; waveCount?: number };
  scheduled_blast_quiet_hours_blocked_ui: { message: string };
  scheduled_blast_rescheduled: { id: string };
  scheduled_blast_canceled: { id?: string; blastGroupId?: string; cancelledWaves?: number };
  scheduled_blasts_card_viewed: { count: number };
  // Duplicate-blast guard: fired when the confirm dialog surfaces the "you already
  // have/sent this exact blast" warning (non-blocking). 2026-07-08.
  duplicate_blast_warned: { blastGroupId: string; status: string; mode: "schedule" | "send" };
  /** Another queued blast is competing for this blast's contacts (see pendingAudienceClaim.ts).
   *  `exhaustsPool` = the queued blasts could take everyone, leaving this one with nobody. */
  blast_pending_claim_warned: {
    competingBlasts: number;
    claimedCount: number;
    eligibleCount: number;
    exhaustsPool: boolean;
    mode: "schedule" | "send";
  };
  // Dashboard-local dismiss of a finished blast row + its Undo (this-browser-only). 2026-06-26.
  scheduled_blast_dismissed: { blastGroupId: string };
  scheduled_blast_dismiss_undone: { blastGroupId: string };
  // Terminal blast outcome — added 2026-06-05 (audit FIX-6: PostHog previously
  // saw previews only; send outcomes were invisible). Fired once when the
  // progress poller sees a terminal status.
  // ⚠️ SNAPSHOT SEMANTICS: a broadcast goes terminal when the last message is
  // ACCEPTED by Twilio; carrier delivered/undelivered callbacks land seconds-
  // to-minutes LATER. So delivered/undelivered here are typically near 0 and
  // pending ≈ total at capture time. Treat this as a "blast submitted, N
  // accepted" signal — durable delivery truth lives in the reporting
  // endpoints, not this event.
  sms_blast_completed: {
    status: string; // COMPLETED | PARTIAL | FAILED | PAUSED_WALLET | CANCELLED
    total: number;
    sent: number;
    failed: number;
    delivered: number;
    undelivered: number;
    pending: number;
    topErrorCode: string | null;
    // Cool-down post-block (2026-11-02).
    filterTier: "none" | "watch" | "severe" | null;
    cooldownShown: boolean;
    autoStopped: boolean;
  };

  // SMS blast wizard funnel — added 2026-05-27 to close PostHog gap from the
  // SMS audit. Lets us track open → preview → confirm → send conversion plus
  // drop-off / failure paths separately from the actual send call.
  sms_wizard_opened: { campaignCount: number };
  sms_blast_previewed: {
    propertyCount: number;
    phoneCount: number;
    estimatedCostCents: number;
    excludedCount: number;
  };
  sms_blast_confirmed: { recipientCount: number; estimatedCostCents: number; noReplyOnly: boolean; advisoryLevel: string | null };
  sms_blast_cancelled: { step: string; reason?: string; advisoryLevel?: string | null };
  // 2026-10-27: the red "Carriers may block this message" box, once per dialog open — so a
  // shown-then-cancelled red is visible (no sms_blast_confirmed after it).
  sms_blast_advisory_shown: { level: string; pillars: string[]; reasonCount: number };
  // 2026-11-02: the composer's "Still N% the same as the text carriers blocked"
  // line crossing to "✓ Different enough" — once per blocked text, on that
  // transition only (never when the draft starts below threshold).
  sms_advisory_different_enough: { similarityPct: number; blockedFilterRatePct: number | null; cooldownShown: boolean };
  sms_exclusion_filter_expanded: Record<string, never>;
  sms_blast_insufficient_balance: { requiredCents: number; availableCents: number; shortfallCents: number };
  sms_blast_topup_clicked: { shortfallCents: number };
  sms_failure_drilldown_viewed: { broadcastId: string; failureCount: number };
  // Carrier-block auto-stop card (2026-10-24): which way the customer went.
  sms_blast_auto_stop_action: { broadcastId: string; action: "edit" | "send_anyway" | "done" };
  // Cool-down post-block (2026-11-02): acting on a scheduled blast waiting behind
  // a blast carriers just blocked, from the CoolingScheduledList rows.
  sms_blast_cooling_scheduled_action: { broadcastId: string; action: "move" | "cancel"; sameMessage: boolean };

  // Campaign follow-ups (Door A) — picking a drip sequence to run after an
  // SMS blast, from the blast wizard's FollowUpsPicker.
  sms_follow_ups_changed: { campaignId: string; sequenceId: string | null };
  sms_failure_retried: { broadcastId: string; messageCount: number; mode: "single" | "batch" };

  // §8 measurement baseline (spec 2026-07-01 SMS blast redesign) — fires once
  // per distinct blocking-state per pageview on /sms-sender. `reason` is the
  // stable BlockReasonKey enum: no_campaign | no_message | incomplete_both |
  // no_sms_path | blast_in_progress | cooldown | daily_limit | number_warmup |
  // selection_gate | no_recipients.
  sms_send_blocked_view: { blockClass: "user-fixable" | "state-blocker"; reason: string };

  // SMS composer attachments (inbox, contact page, Multi-Line modal): one event per attempt
  // to add a photo/video/voice memo, so refusal and failure rates have a denominator.
  // `detail` is the refusal copy or the error message. Never file names or URLs.
  sms_media_attach: {
    surface: "inbox" | "contact" | "contact_modal";
    source: "picker" | "paste" | "drop" | "voice";
    outcome: "attached" | "refused" | "failed";
    reason?: "blocked" | "no_file" | "not_photo" | "busy" | "video_off" | "video_too_big" | "too_big" | "heic" | "decode" | "upload" | "transcode";
    detail?: string;
    inputType?: string;
    inputKb?: number;
    outputKb?: number;
    fileCount?: number;
    status?: number;
  };

  // A photo dragged out of an SMS thread (Chrome gives no file) is fetched from its public storage
  // URL by filesFromDrop. `failed` with status undefined + "Failed to fetch" = the uploads bucket's
  // CORS has no GET (routes/uploads.mjs corsConfig). `stale` = the drop didn't carry the photo's
  // link, so a URL left by a cancelled drag was ignored; common `stale` means a browser that
  // omits text/uri-list on link drags. Never file names or URLs.
  thread_photo_drag: { outcome: "fetched" | "failed" | "stale"; status?: number; detail?: string; contentType?: string; kb?: number };

  // Opportunity stage-picker R2 gate (2026-06-26). Fires when the file-as-
  // Opportunity response can't confirm the badge saved. `status` distinguishes
  // a genuine 5xx (nothing committed) from deploy-skew (2xx/4xx where the old
  // backend committed the placement but didn't echo smsOutcomeSet).
  opportunity_badge_unconfirmed: { propertyId: string; status: number };
  // CRM paywall shown in the Create Opportunity dialog. "plan" = locked on
  // open, so no request is made and the backend's module_gate_blocked never
  // fires; "server" = a 403 MODULE_REQUIRED on submit.
  opportunity_paywall_shown: { propertyId: string; variant: string; source: "plan" | "server" };

  // Wallet topup
  // WI-41: industry-stub waitlist capture (unlaunched verticals).
  waitlist_joined: { vertical: string };

  // Cancel/retention funnel (WI-35, audit M14) — button-level, so "declined"
  // means the user SAW the offer and clicked past it, not merely that the
  // offer step existed. cancel_started → retention_offer_shown →
  // accepted | declined → (cancel POST carries the declined boolean).
  cancel_started: { offerEligible: boolean; downsellEligible?: boolean };
  retention_offer_shown: Record<string, never>;
  retention_offer_accepted: Record<string, never>;
  retention_offer_declined: Record<string, never>;
  // Downsell ("pay less instead of leaving") — shown BEFORE the 50%-off step,
  // so a downsell_accepted means the discount was never spent. `kind` splits
  // the legacy tier downgrade from the v12 module drop.
  downsell_shown: { kind?: string };
  downsell_accepted: { kind?: string };
  downsell_declined: { kind?: string };

  // method "saved_card": the one-click charge on the card on file (no Stripe
  // form); "card_form": the inline Payment Element.
  wallet_topup_started: { amountCents: number; source?: string; method?: "saved_card" | "card_form" };
  // via "redirect": confirmed after a 3DS / bank redirect back to settings/billing
  // (amountCents 0 when the return URL predates the amount param).
  // via "saved_card": charged server-side on the card on file.
  wallet_topup_completed: { amountCents: number; newBalanceCents?: number; via?: "redirect" | "saved_card" };
  // errorCode "element_timeout": the Payment Element never became ready
  // (blocked Stripe frame) — the dialog now says so instead of a greyed-out Pay.
  wallet_topup_failed: { errorCode?: string };

  // Voice agent creation
  voice_agent_creation_started: { template?: string };
  voice_agent_saved: { agentId: string; voiceProvider?: string };
  voice_agent_first_test_call: { agentId: string };

  // STIR/SHAKEN setup nudges (H-stir-shaken). Surfaces nudges across the app
  // when stirShakenStatusEnum is not APPROVED. Carriers flag un-attested calls
  // as "Spam Likely" — these events power the funnel from "saw nudge" →
  // "clicked through" → "completed setup".
  stir_shaken_nudge_shown: {
    surface: "campaigns_banner" | "wallet_card" | "prelaunch" | "modal" | "trust_center";
    status: "NOT_ENABLED" | "PENDING" | "FAILED";
  };
  stir_shaken_nudge_clicked: {
    surface: "campaigns_banner" | "wallet_card" | "prelaunch" | "modal" | "trust_center";
  };
  stir_shaken_nudge_dismissed: {
    surface: "campaigns_banner" | "modal" | "wallet_card";
  };
  // Feedback pulse modal (2026-07) — one-shot "how's it going?" nudge shown
  // to every user once per browser. Funnel: shown → submitted or dismissed.
  feedback_pulse_shown: { surface: "modal" };
  feedback_pulse_submitted: { surface: "modal"; length: number };
  feedback_pulse_dismissed: { surface: "modal" };

  // H3 Lead Sources / Integrations Hub (Trigger Library)
  lead_sources_hub_viewed: {
    totalKeys?: number;
    legacyKeysCount?: number;
    connectedTileCount?: number;
  };
  lead_sources_tile_clicked: { presetId: string; connectedCount?: number };
  lead_sources_generate_clicked: { presetId: string; keysAtCap: boolean };
  lead_sources_key_created: { presetId: string };
  lead_sources_intercept_shown: { presetId: string; closeAffordance: string };
  lead_sources_intercept_confirmed: { presetId: string; closeAffordance: string };
  // Inbound Webhook (leads_hook tiles, HookConnectPanel). Funnel: created → live (→ imported / rotated / removed).
  lead_sources_hook_created: { presetId: string };
  lead_sources_hook_live: { presetId: string; importRequested: number; imported: number; callImported: boolean };
  lead_sources_hook_imported: { presetId: string; importRequested: number; imported: number; callImported: boolean };
  lead_sources_hook_rotated: { presetId: string };
  lead_sources_hook_removed: { presetId: string; pendingCount: number };

  // D1 Source-diagnostic wizard (renders above the tile grid for new tenants).
  // Funnel: viewed → option_picked (or dismissed). hubspot_clicked is the
  // empty-CRM escape hatch path.
  lead_sources_wizard_viewed: { totalActiveKeys: number };
  lead_sources_wizard_option_picked: {
    option: string;
    kind: "preset" | "nothing-yet";
  };
  lead_sources_wizard_dismissed: { from: "grid" | "nothing-yet" };
  lead_sources_wizard_hubspot_clicked: Record<string, never>;

  // Phase 2 PR-2-B — Webhook signing-secret panel inside RecipeDrawer.
  // Funnel: rotated → copied → dismissed. Tracking dismiss separately
  // surfaces customers who rotate but never copy (UX failure that strands
  // them with an unusable plaintext value). `mode` discriminates first-
  // generation (adoption metric) from re-rotation (security metric).
  signing_secret_rotated: { presetId: string; mode: "initial" | "rotate" };
  signing_secret_copied: { presetId: string };
  signing_secret_dismissed: { presetId: string };

  // H4 Lead Sources Playground (/automation/lead-sources/playground)
  lead_sources_playground_viewed: {
    keyCount: number;
    initialPresetId: string | null;
  };
  lead_sources_playground_fire_clicked: {
    presetId: string | null;
    viaKeyboard: boolean;
  };
  lead_sources_playground_fire_success: {
    presetId: string | null;
    hasIgnoredVariables: boolean;
  };
  lead_sources_playground_fire_error: {
    presetId: string | null;
    errorCode: string;
    bucket: 1 | 2;
  };
  lead_sources_playground_addstubs_clicked: {
    presetId: string | null;
    missingCount: number;
  };
  lead_sources_playground_parse_error: {
    hasPosition: boolean;
  };
  lead_sources_playground_unknown_errorcode: {
    errorCode: string;
  };
  // H9 Trial Phone Verification (/onboarding/phone-verify)
  trial_phone_verify_viewed: {
    hasReturnTo: boolean;
  };
  trial_phone_verify_otp_sent: {
    /** Delivery channel the backend used ("call" = voice fallback). */
    channel?: "sms" | "call";
  };
  trial_phone_verify_resent: {
    channel?: "sms" | "call";
  };
  trial_phone_verify_succeeded: {
    hadShortcut: boolean;
  };
  // The dashboard's text/call start got 403 phone_not_verified while the session
  // claimed verified — the JWT claim was stale. The user is sent to the gate,
  // which re-reads the DB. Should be rare; a spike means the claim is going stale
  // for a reason worth finding.
  trial_phone_gate_stale_session: Record<string, never>;
  // Clicked "Sign out and sign in to that account" under a phone_number_in_use
  // refusal on the /verify-phone gate.
  trial_phone_verify_switch_account_clicked: Record<string, never>;
  trial_phone_verify_failed: {
    errorCode:
      | "invalid_request"
      | "invalid_phone_number"
      | "voip_not_allowed"
      | "platform_number"
      | "phone_number_in_use"
      | "code_invalid"
      | "code_expired"
      | "too_many_attempts"
      | "phone_mismatch"
      // Added 2026-09-18. The backend had been returning all three for months;
      // because they were absent from the card's allowlist they arrived here as
      // errorCode:"unknown", which is why the funnel's single largest
      // phone-verify failure class was unattributable.
      //
      // Keep this union in step with `ErrorCode` in PhoneVerificationCard.tsx.
      // The chain is closed at compile time in both directions: KNOWN_B8_CODES
      // is `new Set<B8Code>`, `B8Code` is derived from `ErrorCode`, and
      // `ERROR_MESSAGES[errorCode]` is indexed by it — so a code cannot be
      // allowlisted without a union member, nor given a union member without
      // copy, nor reach track() without an entry here. Until 2026-09-18 that
      // set was `Set<string>` and none of that held: a code could be
      // allowlisted with no copy and the card would render a blank error.
      | "email_not_verified"
      | "unsupported_country"
      | "line_type_unverifiable"
      // 2026-09-18: a Canadian mobile (Lookup 60601). Its own code so the trial
      // funnel can count Canadian signups waiting on a manual verification.
      | "canada_manual_verification"
      | "rate_limited"
      | "network_error"
      | "unknown";
    step: "phone" | "otp";
    /** Only set when errorCode === "unknown". true = 5xx from B8, false = 4xx. */
    serverSide?: boolean;
    /** Raw HTTP status and API code the card classified from (null when the
     *  failure was not an API error). 503 + NETWORK_ERROR = the browser never
     *  reached a server. */
    status?: number | null;
    code?: string | null;
  };
  trial_phone_verify_back_clicked: Record<string, never>;
  trial_phone_verify_cooldown_elapsed_no_resend: Record<string, never>;
  trial_phone_verify_auth_expired: {
    step: "phone" | "otp";
  };

  // A2P + TFV submit hardening (2026-05-30). Typed funnel for the two submit
  // flows that were previously firing through the untyped trackEvent():
  //  - Toll-Free Verification wizard submit (migrated from trackEvent).
  //  - A2P condensed-form COMBINED submit path (previously un-instrumented).
  // Shapes are supersets of the runtime call sites so `next build` type-checks
  // them. `firstError` is optional (reads validationErrors[0], may be undefined);
  // `businessType` allows null (nullable TFV DB column); `errorCode` allows null
  // (the TFV failure path passes `code ?? null`).
  tf_wizard_submit_attempted: {
    verificationId?: string;
    walletBalanceCents?: number;
    verificationFeeCents?: number;
    useCase?: string;
    businessType?: string | null;
  };
  tf_wizard_submit_blocked_validation: {
    verificationId?: string;
    errorCount: number;
    firstError?: string;
  };
  tf_wizard_submit_succeeded: {
    verificationId?: string;
    businessType?: string | null;
    useCase?: string;
    isFixMode?: boolean;
  };
  tf_wizard_submit_failed: {
    verificationId?: string;
    errorCode?: string | null;
    errorMessage?: string;
    failureKind?: "paid_plan_required" | "wallet_insufficient" | "other";
  };
  // Post-checkout hand-off (2026-09-04): which next step the customer took from
  // the subscribe/return page. The `auto:` prefix used to mean we routed them
  // there ourselves; that auto-redirect was removed 2026-09-19 because it sent
  // SMS carts to the A2P application without ever naming the number step, so
  // values are now all user-initiated ("Get approved to text", "Get your
  // number", "Get a number", "Set up your account", "Continue where you left off").
  checkout_next_action_clicked: { action: string };
  // Paid, zero phone numbers (2026-09-19). 9 of 19 cold payers were in this
  // state and 58% of the payers who never reached a prospect cancelled.
  // Pairs with the `paid-no-number` lifecycle email — the banner catches them
  // in-app, the email catches them after they close the tab.
  needs_number_banner_shown: Record<string, never>;
  needs_number_banner_clicked: Record<string, never>;
  a2p_combined_submit_attempted: { wantsTollFree: boolean; registrationType?: string | null };
  a2p_combined_submit_blocked_validation: { failingStep?: string };
  a2p_add_funds_and_submit_clicked: { shortfallCents: number; suggestedUsd: number };
  a2p_combined_submit_succeeded: { a2pStatus?: string; tollFreeStatus?: string };
  a2p_combined_submit_partial_failure: { tollFreeCode?: string; stage: "number" | "tfv" };
  a2p_combined_submit_failed: { errorCode?: string; errorMessage?: string };

  // Global react-query telemetry (2026-05-31). Fired from the QueryCache/
  // MutationCache onError chokepoint in providers.tsx for non-401/429 4xx
  // failures (5xx + unknown go to Error Tracking instead). Powers an
  // "API failures" trend + alerting without polluting Error Tracking with
  // expected client errors. `key` is the queryKey[0]/mutationKey[0];
  // `feature` is the mutation meta.feature when set.
  api_request_failed: {
    kind: "query" | "mutation";
    key?: string;
    feature?: string;
    status?: number;
    code?: string;
    // Set to "network" when the failure is a handled transient connectivity error
    // (rerouted out of Error Tracking by the chokepoint); omitted for generic 4xx.
    reason?: "network";
    /** The request's X-Request-Id — search backend logs for `requestId`. */
    requestId?: string;
  };

  // Activation funnel (2026-05-31) — milestone events the new-user funnel needs
  // that weren't previously instrumented. Segment by the person's `plan`
  // property (stamped by PostHogIdentifyEnricher), so plan isn't duplicated on
  // each event payload.
  phone_number_purchased: {
    numberType: "TOLLFREE" | "LOCAL";
    sipTrunkFailed?: boolean;
    isFirstNumber?: boolean;
    // Server-reported A2P attach outcome (null = SMS not requested / TF /
    // older backend). Reason values: services/a2p/smsAttachmentOutcome.mjs.
    smsAttached?: boolean | null;
    smsAttachReason?: string | null;
    /** Set on a bulk purchase, with the quantity bought. */
    bulk?: boolean;
    count?: number;
  };
  /** A number's status toggled via Flag / Reactivate on the Phone Numbers page. */
  phone_number_status_changed: {
    status: "flagged" | "active";
    phoneNumberId: string;
  };
  capability_assigned: {
    calling: boolean;
    sms: boolean;
    browser: boolean;
    isFirstCapability?: boolean;
  };
  campaign_created: { channel?: string };
  voice_trust_verified: Record<string, never>;

  // New-user onboarding fork wizard (A3). Happy-path funnel: fork_shown →
  // branch_selected → test action → customize → branch_completed → wizard_completed.
  // Failure keys capture drop-off without polluting Error Tracking.
  onboarding_fork_shown: Record<string, never>;
  onboarding_branch_selected: { branch: "SMS" | "VOICE" };
  onboarding_wizard_skipped: Record<string, never>;
  // Mid-branch exit (fork already resolved) — distinct from the pre-fork skip.
  onboarding_branch_abandoned: { branch: "SMS" | "VOICE" | null };
  onboarding_test_sms_clicked: Record<string, never>;
  onboarding_test_call_clicked: Record<string, never>;
  onboarding_customize_opened: { branch: "SMS" | "VOICE" };
  onboarding_customize_saved: { branch: "SMS" | "VOICE" };
  onboarding_branch_completed: { branch: "SMS" | "VOICE" };
  onboarding_second_branch_nudge_clicked: { from: "SMS" | "VOICE"; to: "SMS" | "VOICE" };
  onboarding_wizard_completed: { firstBranch: "SMS" | "VOICE" };
  onboarding_entry_error: { kind: string };
  onboarding_test_sms_failed: { code: string };
  onboarding_test_call_failed: { code: string };
  // First-agent playground → "call my phone with this agent" demo (CallMyPhoneDialog).
  playground_call_me_clicked: { agentId: string };
  playground_call_me_failed: { code: string };
  onboarding_call_poll_ended: { reason: "ceiling" | "failures" | "not_found" };
  onboarding_customize_failed: { kind: string };
  // WI-28: aha SUCCESS events — without these the flip's A/B readout can't
  // distinguish "aha failed" from "aha succeeded, user left".
  onboarding_test_sms_sent: Record<string, never>;
  onboarding_test_call_connected: { outcome: string | null };
  // Wizard-scoped phone verification (the trial_phone_verify_* family fires
  // from the shared card regardless of surface; this one is wizard-only).
  onboarding_phone_verified: { branch: "SMS" | "VOICE" };

  // SMS plan-cap trend nudge (SmsCapTrendNudge, Task 4) — pitches an upgrade to
  // tenants who've been repeatedly PLAN-cap-blocked (>=3 days in the trailing
  // 30). shown fires once per mount when the gates pass; upgrade_clicked and
  // dismissed are user actions on the card.
  sms_cap_nudge_shown: { capHitDays30d: number; tier: string };
  sms_cap_nudge_upgrade_clicked: { capHitDays30d: number; tier: string };
  sms_cap_nudge_dismissed: { capHitDays30d: number; tier: string };

  // Change 3 / Task 3c — flag-gated activation-first dashboard zero-state
  // (NEXT_PUBLIC_ONBOARDING_DASHBOARD_V2). `shown` fires once per mount when
  // the slim layout activates; `first_win_card_clicked` distinguishes which
  // of the 3 hero actions drove the click. (`readiness_expanded` was removed
  // with the readiness checklist — its call site is gone.)
  onboarding_dashboard_v2_shown: Record<string, never>;
  onboarding_first_win_card_clicked: { card: "sandbox" | "test_call" | "campaign" };

  // Trial Onboarding Overhaul (spec 2026-08-30) §2.8 — intro tour (Plan A).
  // step is 0-based, matching INTRO_TOUR_STEPS ordering.
  //
  // Naming note (audit N2): spec §2.8 lists this group as
  // "tour_step_viewed/completed/skipped", but the "completed" member is
  // NOT a per-step event — it fires exactly once, when the LAST step's
  // "Try it now" is clicked (see SpotlightEngine.tsx: `emit({ type:
  // "completed", ... })` only happens in the `isLast` branch). Naming it
  // `tour_step_completed` alongside two genuinely per-step siblings
  // (`_viewed`/`_skipped`, which fire once per step shown/skipped) would
  // misrepresent it as recurring. Named `tour_completed` here instead; the
  // spec's §2.8 listing should be read with this correction in mind.
  // `tour` identifies which tour emitted the event: "intro" (trial dashboard
  // tour), "platform" (paid welcome tour, spec 2026-09-03), "page:<slug>".
  // `modules`/`preset` (spec §10) are OPTIONAL — only the paid platform tour
  // populates them (owned v11 modules + the derived v12 preset name); the
  // trial "intro" tour and page tip tours omit both.
  tour_step_viewed: { step: number; target: string; tour: string; modules?: string[]; preset?: string };
  tour_completed: { step: number; target: string; tour: string; modules?: string[]; preset?: string };
  tour_step_skipped: { step: number; target: string; reason: "anchor_miss" | "closed" | "esc"; tour: string; modules?: string[]; preset?: string };
  // Not in spec §2.8's event list — added so a failed tour-state persistence
  // call (audit S12) is observable rather than a silent no-op. See page.tsx's
  // handleTourEvent, which fires this from the .catch() on both the
  // "completed" and "dismissed" POST /tour-state calls.
  tour_persist_failed: { event: "completed" | "dismissed" | "seen"; tour: string };
  // Paid platform tour only (spec §10).
  tour_cta_clicked: { tour: string; step: number; cta: string };
  tour_replayed: { tour: string };
  // Checkout return card (2026-09-04): which first action the customer took.

  // Trial Onboarding Overhaul Plan C, Task 6 — sequence hero's "Call me"
  // demo-call button. `_started` fires on click (before the request
  // resolves); `_succeeded`/`_failed` discriminate the POST /demo-call/start
  // response; `_exhausted_shown` fires instead of `_failed` specifically for
  // the demo_call_exhausted code, since that path is a deliberate dead-end
  // UI state (step 2 becomes unavailable, step 3 unlocks) rather than a
  // generic error. Only the events this batch's code actually fires are
  // declared here — a later task adds the remaining demo-call event family.
  demo_call_started: Record<string, never>;
  demo_call_start_succeeded: Record<string, never>;
  demo_call_start_failed: { code: string };
  demo_call_exhausted_shown: { code: string };

  // Trial Onboarding Overhaul Plan C, Task 8 — the cross-page reveal ("See
  // what Ava just did →"), driven by RevealSpotlight.tsx. `reveal_started`
  // fires once, from page.tsx's handleReveal, when the walk begins;
  // `reveal_step` covers per-stop user actions (Next/Back button, Esc, or a
  // manual browser-back/forward mid-reveal); `reveal_step_skip` is the
  // reveal's own anchor-miss telemetry (spec §2.6 requires this be
  // non-silent, unlike the intro tour's silent skip); `reveal_completed`
  // fires once, on the final "Back to your dashboard" click;
  // `reveal_blocked_no_property` is handleReveal's defensive B3 guard firing
  // (should be unreachable in practice — WhatAvaNotedCard's revealDisabled
  // prop already keeps the button from being clickable in this state — but
  // this is Controller ruling C-R5's own EventMap gap-closure: the plan's
  // Task-10 EventMap list never declared it despite Task 8's code firing
  // it). A later task adds the remaining demo_* events.
  reveal_started: Record<string, never>;
  reveal_step: { page: "dashboard-cta" | "opportunities" | "notes"; action: "next" | "esc_exit" | "nav_exit" };
  reveal_step_skip: { page: "dashboard-cta" | "opportunities" | "notes"; reason: "anchor_miss" };
  reveal_completed: Record<string, never>;
  reveal_blocked_no_property: Record<string, never>;
  // Resumable-reveal hardening (2026-09-08): `reveal_step_shown` fires once a
  // stop's anchor is found and the popover lands; `reveal_closed` covers
  // every closeReveal() call site with WHY it closed (esc/nav/completed/the
  // anchor-miss fallback's own Skip-with-no-host path); `reveal_anchor_miss_fallback`
  // is the anchor-miss dead-end card's two buttons.
  reveal_step_shown: { page: string };
  reveal_closed: { page: string; reason: "esc_exit" | "nav_exit" | "completed" | "anchor_miss" };
  reveal_anchor_miss_fallback: { page: string; action: "open" | "skip" };

  // Trial Onboarding Overhaul Plan C, Task 10 — the remainder of the §2.8
  // client event set this plan owns. `demo_text_started` fires at
  // SequenceTextMirror's startSmsDemo() call site; `demo_reply_received`
  // fires alongside its onReplied callback (S27's accepted narrow gap
  // applies here too — see that component's comment). `demo_meeting_booked`
  // fires from page.tsx on sequence.meetingBooked's false→true transition.
  // (`conversion_card_viewed`/`_clicked` were deleted 2026-09-07 with
  // SequenceConversionCard — showcase-first removed the card; the upgrade
  // ask is `onboarding_upgrade_*` below.) `demo_call_completed` is Plan B's own
  // server-side log event, not a client EventMap entry (audit S19) — not
  // duplicated here.
  demo_text_started: Record<string, never>;
  demo_text_start_failed: { code: string };
  demo_reply_received: Record<string, never>;
  demo_meeting_booked: Record<string, never>;
  // 2026-09 first-win → recap flow instrumentation. `sequence_spotlight_shown`
  // fires when the forced scrim gates a step (the only way to measure the
  // forced funnel + abandonment under the force). `demo_call_answered` makes
  // "answered but didn't book" a distinct funnel drop. `onboarding_recap_started`
  // (resumed=true when it starts from a fresh load after a close, not the
  // in-session transition) makes the recap → nav-tour resume rate countable.
  sequence_spotlight_shown: { step: 1 | 2 };
  demo_call_answered: { booked: boolean };
  // `booked` (2026-09-27): the recap now runs for unbooked demo calls too — it
  // used to be gated on meetingBooked, which was true for 100% of answered
  // demos because the end-of-call fallback always wrote an Appointment. This
  // splits the funnel into "recap after a real booking" and "recap after a call
  // that booked nothing", which is the cohort the demo-resilience work is aimed
  // at and was previously indistinguishable.
  onboarding_recap_started: { resumed: boolean; booked: boolean };
  // Stall / give-up signals (observability audit 2026-09-06). These are the
  // moments the UI already KNOWS the user is stuck but previously said nothing
  // to PostHog — the two highest-probability cold-traffic drop points.
  // `demo_text_no_reply_shown` = the 2-minute "Didn't get it?" fallback
  // rendered; `_resend_clicked` / `_try_call_clicked` are its two buttons.
  // `demo_call_timed_out` = the retry/skip row appeared: the call ended
  // unanswered ("Your phone didn't pick up", elapsedMs < 180000) or hit the
  // 180s cap ("Didn't connect in time");
  // `demo_call_retry_clicked` its Try again. `sequence_step_stalled` = a step
  // has been the active one for 5 minutes with no progress (fires once per
  // step per mount). `onboarding_status_failed` = GET /onboarding/status
  // errored — the global api_request_failed chokepoint drops 401/429 and keys
  // 4xx only as `onboardingStatus`, so this is the findable, alertable copy.
  demo_text_no_reply_shown: Record<string, never>;
  demo_text_resend_clicked: Record<string, never>;
  /** `reason: "text_failed"` = offered because the text never went out, not because of no reply. */
  demo_text_try_call_clicked: { reason?: "text_failed" };
  demo_call_timed_out: { elapsedMs: number; callId: string };
  demo_call_retry_clicked: Record<string, never>;
  // 2026-09-07 run-1 fixes. `demo_call_adopted` = /start's response was lost
  // (proxy timeout / 409 / network) but /latest found the call that rang, so
  // the dashboard watched it instead of showing an error. `demo_call_skipped`
  // = the user took "Skip — keep going" after a failed/timed-out/exhausted
  // call (the tour continues). `demo_text_completed` = the SMS conversation
  // ended; `booked` = the agent scheduled the call from the text thread.
  demo_call_adopted: { reason: string };
  // First live-transcript utterance reached the dashboard during the demo call —
  // proves the transcript_update pipe works for cold traffic, and how fast.
  demo_call_transcript_first_entry: { msSinceStart: number };
  // "user_skipped" = the step-2 "Skip for now" link: nothing failed and nothing
  // ran out, the user chose not to take the call.
  demo_call_skipped: { reason: "timed_out" | "failed" | "exhausted" | "user_skipped" };
  demo_text_completed: { booked: boolean };
  // `forced` = the click-blocking overlay was up when the timer fired;
  // `visible` = the tab was in front. Without them an idle dashboard tab left
  // open by a returning trial account paged exactly like someone held behind
  // the overlay (one account, three pages in a week, 2026-10-02 to 10-08).
  sequence_step_stalled: { step: 1 | 2 | 3; forced?: boolean; visible?: boolean };
  // 🚨 A click that the forced spotlight ABSORBED (2026-09-29). Prod user
  // st.henrymngt@gmail.com dead-clicked the bottom band twice in his first
  // minute, ~3s of waiting each, before finding the only live control. The
  // bands still block — that is their job — but they now nudge instead of
  // sitting silent, and this is how we find out whether the nudge works:
  // a rising count with no matching rise in sequence_skipped means the hero
  // is not the obvious target we assume it is.
  sequence_scrim_click_blocked: { step: 1 | 2 };
  /** The step-1 escape hatch was taken — the forced spotlight releases for this
   *  session. Revealed by the SAME STEP_STALL_MS timer that fires
   *  `sequence_step_stalled {step:1}`, so take-rate is simply this count over that
   *  one; no separate "hatch shown" event. No payload: the hatch only exists at
   *  step 1, so the name already carries the step. */
  // `step` added 2026-09-27 when step 2 got its own exit. Optional so the
  // step-1 call site (which has never sent it) stays valid and the funnel's
  // existing take-rate maths — sequence_skipped over sequence_step_stalled —
  // keeps working unchanged.
  sequence_skipped: { step?: 1 | 2 };
  /** A step-1 skip ended because the text demo finished anyway, so step 2's
   *  "Call me" spotlight is back (2026-10-21). Count of skippers re-guided to the call. */
  sequence_skip_released: { reason: "text_completed" };
  onboarding_status_failed: { status: number | null; code: string | null };
  /** Same failure, but the request never reached a server (browser
   *  NETWORK_ERROR / REQUEST_TIMEOUT). Deliberately NOT in the funnel alert. */
  onboarding_status_unreachable: { status: number | null; code: string | null };
  // Post-recap convert-or-explore modal (2026-09). `_prompt_shown` fires when
  // the modal opens after the recap; `_upgrade_clicked` / `_keep_exploring`
  // are the two choices — the latter starts the interim nav tour.
  onboarding_upgrade_prompt_shown: Record<string, never>;
  onboarding_upgrade_clicked: Record<string, never>;
  onboarding_keep_exploring: Record<string, never>;
  // Trial dashboard "View guided tour" replay button — re-runs the cross-page nav tour.
  onboarding_tour_replayed: Record<string, never>;
  // Trial "Clear sample data" button — manual demo-data purge.
  demo_data_cleared: Record<string, never>;
  // Trial "Show on dashboard" switch (SampleDataCard, trial funnel run 4 Part D).
  sample_view_toggled: { on: boolean };

  // AVA "sample data" empty state (zero-data accounts) — impression event for
  // the labeled example output shown until the tenant has real call/SMS data.
  ava_sample_state_viewed: Record<string, never>;

  // Dashboard merged Inbox card (SMS dashboard redesign PR-1, Task 5) —
  // conversation_opened mirrors NeedsYouNowCard's popout analytics across
  // both pinned (needs_human_card) and feed (dashboard_activity) sources.
  dashboard_inbox_conversation_opened: { source: "needs_human_card" | "dashboard_activity" };
  dashboard_inbox_view_all_clicked: Record<string, never>;

  // "In motion" section (PR-2). viewed fires once per non-empty render batch;
  // primaryKind is the tier winner so we can see what usually tops the stack.
  dashboard_in_motion_viewed: { count: number; primaryKind: string };
  dashboard_in_motion_expanded: Record<string, never>;
  dashboard_in_motion_action: { kind: string; action: "edit" | "cancel" | "view_flow" | "add_funds" };

  // Calendar GA (2026-09-16): which starting point a tenant picks from the
  // "set your working hours" prompt. The split between weekdays and 7-day is
  // the one thing worth knowing on GA day — it says whether the placeholder
  // Mon–Fri week was ever the right default for this customer base.
  working_hours_preset_set: { preset: "weekdays" | "every-day" };

  // Caller ID (Voice Trust) — a re-submit landing on 409 ALREADY_SUBMITTED.
  // Not an error (we advance the wizard), but each one is a user who didn't
  // believe their submission had gone through. A rising count means the
  // success state still isn't reading clearly.
  voice_trust_already_submitted: Record<string, never>;

  // Get Started hub. This surface exists to move users through activation, so
  // shipping it blind means we cannot tell whether it works or which step
  // people stall on. registrationType rides along on all of them because the
  // per-tier copy is the thing most likely to change behaviour.
  get_started_hub_viewed: {
    /**
     * "exploration" (pre-billing — no UserSubscription row yet, which is every
     * new signup) or "post_subscription". Added 2026-08-07 when the hub was
     * opened to the exploration phase; without it the two cohorts are
     * indistinguishable in PostHog and hub adoption can't be read per-phase.
     *
     * The exported union, not `string`: a typo'd literal would ship silently
     * and quietly break the per-cohort breakdown this field exists for.
     */
    phase: Phase;
    /**
     * Splits the exploration cohort three ways — a day-one signup, a customer
     * who cancelled, and a suspended account are all `phase: "exploration"`
     * but are three different funnels.
     */
    subscriptionState: SubscriptionState;
    registrationType: string | null;
    usesCalls: boolean;
    smsApproved: boolean;
    completed: number;
    total: number;
  };
  // Post-A2P flow (2026-09-04): the one-click next action in the hub header while in review.
  get_started_in_review_cta_clicked: { cta: string };
  get_started_submitted_welcome_shown: { registrationType?: string | null };
  automations_guide_started: { kind: "automation" | "drip"; surface: "list" | "builder" };
  automations_guide_ended: { kind: "automation" | "drip"; surface: "list" | "builder"; outcome: "completed" | "skipped"; step: number };
  get_started_submitted_welcome_cta_clicked: { nextId: string | null };
  get_started_number_purchased_return: { numberType: string; fromHub: boolean };
  get_started_step_expanded: {
    stepId: string;
    locked: boolean;
    /**
     * WHY it was locked, not just that it was. "smsApproval" is a healthy
     * carrier queue; "subscription" is a conversion stall. Aggregating them
     * into one boolean hides the only one of the two we can act on.
     */
    lockReason: LockReason;
    registrationType: string | null;
  };
  get_started_video_played: {
    videoId: string;
    title: string;
    category: string;
    source: "upload" | "loom";
    /** "step" = opened from a checklist row, "link" = arrived on a `?video=` link. */
    surface: "step" | "library" | "link";
    stepId?: string;
    registrationType: string | null;
  };
  /** `category: null` = back to All. */
  get_started_category_filtered: { category: string | null };
  /**
   * A hosted video failed to load in a user's browser — the object was deleted,
   * lost its public ACL, or the bucket is unreachable. Nothing server-side sees
   * this (the browser talks to GCS directly), so this event is the only signal.
   */
  get_started_video_playback_failed: { videoId: string; title: string };
  get_started_cta_clicked: {
    stepId: string;
    href: string;
    registrationType: string | null;
  };
  get_started_catalog_fetch_failed: Record<string, never>;
  /**
   * A STORED thumbnail failed to load in a user's browser — the Loom hash
   * rotated, the video was deleted, or the CDN object went away.
   *
   * This is the one failure mode that resolving at save time makes WORSE. When
   * the thumbnail was resolved per-session it self-healed on the next visit;
   * stored, a dead thumbnail is permanent and completely invisible — the page
   * silently returns to the black-rectangle state the feature exists to fix.
   * This event is the only signal that has happened. Recovery is re-uploading
   * the video (or, for a hosted one, picking a new thumbnail) in the admin console.
   *
   * Keyed on the LOOM ID, not a step id: VideoPlayer renders from both the
   * checklist (StepRow) and the library modal, and only the library one has no
   * step id at all. Do not rename this to `stepId` — it will not join against
   * step keys.
   */
  get_started_thumb_load_failed: {
    loomId: string | null;
    /** Set for hosted videos, which have no Loom id to join on. */
    videoId?: string;
    title: string;
  };
  /**
   * A DIRECT arrival at /get-started that the paid gate bounced — a bookmark, a
   * typed URL, or a stale link from when the hub was open to pre-billing
   * accounts (2026-08-07 → 2026-08-10).
   *
   * NOT a regression detector, despite the temptation to read it as one. If the
   * backend stops emitting `hubEligible`, the sidebar entry and dashboard
   * callout disappear, so nobody navigates here and this event goes QUIET rather
   * than loud. The backend's `get_started_hub_gate` log line is what catches
   * that; this counts how much bookmark traffic the gate is turning away.
   */
  get_started_hub_blocked: {
    /** `null` = the field was absent: flag off, or a frontend-ahead-of-backend deploy. */
    hubEligible: boolean | null;
    /** Splits the bounced cohort — a trial, a cancellation and a suspension are three funnels. */
    subscriptionState: SubscriptionState | null;
    /** Separates paid-gate bounces from kill-switch bounces. Filter on it before reading the rest. */
    flagEnabled: boolean;
  };

  // ── Acquisition funnel (2026-08-29 cold-traffic audit) ────────────────────
  // Before these, the leg from "clicked Start free trial" to "paid" emitted
  // nothing: no signup event, no register failure, no pricing view, and the
  // Stripe form mounts at the same pathname as the plan grid so not even a
  // $pageview separated them. 67 checkout sessions were invisible to PostHog.
  /** Fired once per account by PostHogTracker, after identify (see there). */
  user_signed_up: { method: "credentials" | "google" | "unknown" };
  /** /api/register rejected the form, the network failed, or OAuth bounced back with ?error=. */
  /** `edge_blocked` = a 403/429 with no JSON body, i.e. the Vercel Firewall answered, not /api/register. */
  registration_failed: {
    method: "credentials" | "google";
    status?: number;
    reason: string;
    edge_blocked?: true;
    mitigated?: string;
    vercel_id?: string;
  };
  /** The subscribe page rendered its plan surface. `cohort` is which surface. */
  pricing_viewed: {
    cohort: "modules" | "core" | "legacy";
    tier: string | null;
    /** "deeplink" = arrived with ?tier= (which auto-selects on legacy cohorts). */
    entry: "deeplink" | "direct";
    manageMode: boolean;
  };
  plan_selected: {
    plan: string;
    modules: string[];
    interval: "month" | "year";
    entry: "deeplink" | "click";
  };
  /** A Stripe client secret was obtained — the embedded card form is about to mount. */
  checkout_form_shown: { plan: string };
  /** checkout-session creation threw before the form could mount. */
  checkout_session_failed: { plan: string; reason: string };
  checkout_completed: { sessionId: string };
  checkout_expired: { sessionId: string };

  // Multi-Line Dialer rework (2026-09-28): pause, per-line hangup, and the
  // contact-modal-while-dialing flow.
  multiline_pause: { sessionId: string; paused: boolean };
  multiline_line_hangup: { sessionId: string; attemptId: string };
  multiline_contact_modal_opened: { sessionId: string; propertyId: string };
  multiline_sms_sent: { sessionId: string; propertyId: string };

  // Multi-line observability (2026-10-22). `sessionId` is also a super-property
  // (setCorrelationIds) — it is repeated here so a single event is self-describing.
  multiline_session_started: {
    sessionId: string;
    lineCount: number;
    campaignCount: number;
    autoDialEnabled: boolean;
    autoDialDelay: number;
  };
  multiline_session_ended: {
    sessionId: string;
    by: "agent" | "server";
    /** Away taking an inbound call when it ended (Pause dialing & answer). */
    away?: boolean;
    /** Ended from the error banner after a recovery couldn't attach (stats unknown). */
    orphan?: boolean;
    contactsDialed: number;
    contactsReached: number;
    totalCalls: number;
  };
  multiline_session_recovered: { sessionId: string; forced: boolean };
  /** Another tab or window recovered this session — this tab's ringing lines were hung up. */
  multiline_session_taken_over: { sessionId: string };
  /** A dialer tab opened while another tab of this browser holds the session; nothing was recovered. */
  multiline_session_open_elsewhere: Record<string, never>;
  /** The rep moved that session to this tab ("Use it here"). */
  multiline_session_moved_here: Record<string, never>;
  multiline_batch_dialed: {
    sessionId: string;
    linesRequested: number;
    linesDialed: number;
    contacts: number;
    attemptIds: string[];
    batchFinished: boolean;
  };
  /** A `/dial` that came back `ok: false` — no candidates, no usable numbers, or another refusal. */
  multiline_dial_refused: {
    sessionId: string;
    reason: string;
    retryInSeconds: number | null;
    lineCount: number;
    requestId?: string;
    poolReason?: string;
    linesWanted?: number;
    linesDialing?: number;
    dialersOn?: number;
    dialersOff?: number;
    resting?: number;
    cappedToday?: number;
    cappedSession?: number;
    blocked?: number;
  };
  multiline_pool_warning_shown: {
    kind: string;
    level: string;
    surface: "pre_session" | "in_session" | "dial_refused";
  };
  multiline_pool_cta_clicked: {
    kind: string;
    cta: string;
    surface: "pre_session" | "in_session" | "dial_refused";
  };
  multiline_settings_changed: {
    setting: "autoDialEnabled" | "autoDialDelay" | "defaultLineCount";
    value: number | boolean;
  };
  multiline_outcome_recorded: {
    sessionId: string;
    attemptId?: string;
    propertyId?: string;
    outcome: string;
    owed: boolean;
  };
  multiline_sse_degraded: { sessionId: string; attempts: number; readyState: number };
  /** The agent's leg dropped and was NOT auto-rejoined: the "Rejoin call audio" banner is up. */
  predictive_audio_lost: {
    sessionId: string;
    reason: "device_not_registered" | "rejoin_budget_exhausted";
    rejoinAttempts: number;
  };
  /** Start session refused because another flow's call is live on the shared device. */
  predictive_session_blocked_active_call: Record<string, never>;
  /** A Twilio Voice code that means the agent's mic or network, not our code (twilioDevice.ts). */
  twilio_device_environment_error: {
    code: number;
    scope: "device" | "call";
    feature: "predictive-dialer" | "inbound";
  };
  /** A Telnyx WebRTC code that means the person's mic or network, not our code (telnyxDevice.ts, T7b). */
  telnyx_device_environment_error: { code: number; scope: "session" | "call" };
  /** Pause dialing & answer: one event per step, so a stuck callback shows where it stopped. */
  multiline_callback_pause_answer: {
    sessionId: string;
    inboundCallId?: string;
    stage: "clicked" | "parked" | "park_failed" | "answered" | "answer_failed" | "resumed" | "resume_failed" | "ended_while_away" | "refused_live_call";
  };
  /** A call that rang during a Multi-line session and wasn't answered. */
  multiline_callback_missed: {
    sessionId: string;
    inboundCallId: string;
    reason: "declined" | "voicemail" | "no_answer" | "timeout" | "caller_hangup";
    bridged: boolean;
  };

  // Contact page number picker. A rep narrowed Conversation / Notes / Activity to one of the
  // contact's numbers (scoped: true) or went back to "All numbers" (scoped: false). Counts
  // only — never a phone number. It lets a "my texts / notes vanished" report be checked
  // against whether the rep had the view narrowed at the time.
  contact_number_scope_changed: { scoped: boolean; numbers: number };

  // Contact page Previous/Next (opened from the Contacts list or a campaign table). Is it used,
  // and do reps walk past one list page (crossedPage: the step had to load more ids)?
  contact_nav_clicked: { direction: "next" | "previous"; crossedPage: boolean };

  // CSV upload wizard: fired when the customer leaves the Columns step (once per distinct
  // mapping). The backend never sees the columns the upload LEAVES OUT (unmapped and not
  // kept), so this is the only way to tell "we lost the customer's notes column" from "the
  // file had none" (Amir, 2026-09-29). `droppedNotesLikeHeaders` holds ONLY dropped headers
  // that contain a notes word; free-form header names are never sent, because a file with
  // no header row makes the first DATA row the "header" (names, phones, sentences).
  csv_columns_summary: {
    mappedCount: number;
    unmappedCount: number;
    keptCount: number;
    droppedCount: number;
    /** Dropped columns whose header is blank (a trailing comma or an untitled column).
     *  They cannot be picked in the contacts mapper, so this tells "left out a real
     *  column" from "the file had an empty one". */
    droppedUntitledCount: number;
    hasNotesTarget: boolean;
    droppedNotesLikeHeaders: string[];
    /** A column feeds First / Last / Full Name. false + contactsOnly = a nameless list. */
    hasNameTarget: boolean;
    /** The customer was asked (a left-out column held data and nothing fed the name) and
     *  ticked "Import these contacts without names". */
    importWithoutNamesTicked: boolean;
    contactsOnly: boolean;
  };

  // /sms-sender audience presets (D3): a click on "Ready to text" / "Follow up with
  // them" / the queue sheet's CTA / the exhausted card's CTA. `source` distinguishes
  // the four entry points so the funnel can tell which one people actually use.
  sms_blast_audience_preset_applied: {
    preset: AudiencePreset;
    source: AudiencePresetSource;
    campaignId: string | null;
    /** How many filters the user had set that the preset cleared (named in the toast, with Undo). */
    clearedFilters: number;
  };

  // ── Seats: adding a team member (RUNBOOK-seats "Watching a first seat"; PostHog dashboard
  // "Seats: add a team member"). The backend's team_* / seat_* lines in Cloud Logging carry the
  // same journey with ids; these say what each person SAW.
  //
  // Owner, guided add (/settings/team/add). The steps are client state, so pageviews can't tell
  // them apart. fixKind = canInvite.fix.kind on a blocked plan step (the one next step shown).
  team_add_step_viewed: { step: "plan" | "setup" | "review"; allowed: boolean; fixKind: string | null };
  team_add_multiline_result: { ok: boolean; code: string | null };
  // emailSent false = the invitation was saved but its email didn't send ("We couldn't send the email").
  // abilities / limitCents are null on a Resend from the Team page (it sends only the invitation id).
  team_invite_sent: { resent: boolean; emailSent: boolean; abilities: number | null; limitCents: number | null };
  team_invite_send_failed: { resent: boolean; code: string | null; status: number | null };
  // A warning on the Team page: once per notice per page load (the 60 s refetch re-sends nothing).
  team_page_notice_shown: { notice: TeamPageNotice };
  // Invitee (/invite/accept). PostHog starts there only after the token is out of the address bar.
  // state = "form", "signed_in_as_other", or the refusal reason (expired, cancelled, accepted, …).
  team_invite_page_viewed: { state: string };
  team_invite_join_submitted: { method: "password" | "google" };
  // reason = the backend's refusal, "password_rule", "invalid_input" (a 400, e.g. no name) or "unavailable".
  team_invite_join_refused: { method: "password" | "google"; reason: string };
  // signedIn false = the login exists but the automatic sign-in didn't finish (sent to /login).
  team_invite_joined: { method: "password"; signedIn: boolean };
  // Member, in their app: a notice above their page.
  team_member_notice_shown: { notice: "paused" | "not_in_login" };
}

export type TeamPageNotice = "trial" | "paused" | "needs_multiline" | "charge_declined";

export function track<K extends keyof EventMap>(
  event: K,
  properties?: EventMap[K]
): void {
  const ph = getPostHog();
  if (!ph) return;
  // PostHog's capture signature is non-generic — cast through AnyProps to
  // satisfy TypeScript without losing the typed call site.
  ph.capture(event, properties as AnyProps | undefined);
}
